import { useState, useEffect } from "react";
import { ArrowLeft, Truck, Loader2, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface CarrierMatch {
  truckId: string;
  companyName: string;
  equipmentType: string;
  originLocation: string;
  destinationLocation: string;
  availableFrom: string;
  availableTo: string;
  ratePerMile: number | null;
  matchedLoadRef: string;
}

interface CarrierMatchesPaneProps {
  onBack: () => void;
}

export function CarrierMatchesPane({ onBack }: CarrierMatchesPaneProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [matches, setMatches] = useState<CarrierMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetchMatches = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        // 1. Get user's active loads
        const { data: loads } = await supabase
          .from("loads")
          .select("id, load_number, equipment_type, origin_state, pickup_date")
          .eq("created_by", user.id)
          .eq("status", "posted")
          .gte("pickup_date", today);

        if (!loads || loads.length === 0) {
          setMatches([]);
          setLoading(false);
          return;
        }

        // 2. Get active truck postings that overlap dates
        const equipmentTypes = [...new Set(loads.map(l => l.equipment_type))];

        const { data: trucks } = await supabase
          .from("truck_postings")
          .select("id, equipment_type, origin_location, destination_location, available_from, available_to, rate_per_mile, company_id, companies(name)")
          .in("status", ["active", "available"])
          .gte("available_to", today)
          .neq("created_by", user.id);

        if (!trucks || trucks.length === 0) {
          setMatches([]);
          setLoading(false);
          return;
        }

        // 3. Match logic: equipment type + date overlap
        const results: CarrierMatch[] = [];
        const seen = new Set<string>();

        for (const load of loads) {
          for (const truck of trucks) {
            // Equipment match (normalize both to lowercase for comparison)
            const truckEq = (truck.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
            const loadEq = (load.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
            if (truckEq !== loadEq) continue;

            // Date overlap: truck available range covers the pickup date
            if (load.pickup_date < truck.available_from || load.pickup_date > truck.available_to) continue;

            // Origin state proximity (extract state abbreviation from free-text origin_location)
            const truckOriginState = extractState(truck.origin_location);
            if (truckOriginState && load.origin_state && truckOriginState !== load.origin_state) continue;

            // Deduplicate by truck id
            if (seen.has(truck.id)) continue;
            seen.add(truck.id);

            const companyData = truck.companies as any;

            results.push({
              truckId: truck.id,
              companyName: companyData?.name || "Independent Carrier",
              equipmentType: formatEquipment(truck.equipment_type),
              originLocation: truck.origin_location,
              destinationLocation: truck.destination_location,
              availableFrom: truck.available_from,
              availableTo: truck.available_to,
              ratePerMile: truck.rate_per_mile,
              matchedLoadRef: load.load_number,
            });
          }
        }

        setMatches(results.slice(0, 21));
      } catch (err) {
        console.error("CarrierMatchesPane error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [user]);

  const handleInvite = (match: CarrierMatch) => {
    toast({
      title: "Invite sent (simulated)",
      description: `Carrier "${match.companyName}" would be notified about load ${match.matchedLoadRef}.`,
    });
  };

  return (
    <div className="flex flex-col gap-0 h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to My Loads
        </button>
        <span className="text-[10px] text-muted-foreground">|</span>
        <div className="flex items-center gap-1.5">
          <Truck className="h-3.5 w-3.5 text-success" />
          <h4 className="text-caption font-bold text-foreground">AI Carrier Matches</h4>
        </div>
        <span className="ml-auto text-[10px] text-muted-foreground">{matches.length} result{matches.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[12px]">Matching carriers to your loads…</span>
          </div>
        ) : matches.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Truck className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-[13px] font-semibold text-muted-foreground">No matching carriers found</p>
            <p className="text-[11px] text-muted-foreground">Post loads or check back later as new trucks become available.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Carrier</TableHead>
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Equipment</TableHead>
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Origin</TableHead>
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Available</TableHead>
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">RPM</TableHead>
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Matched Load</TableHead>
                <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {matches.map((m) => (
                <TableRow key={m.truckId} className="group">
                  <TableCell className="px-3 py-2 text-[11px] font-medium text-foreground">{m.companyName}</TableCell>
                  <TableCell className="px-3 py-2 text-[11px] text-muted-foreground">{m.equipmentType}</TableCell>
                  <TableCell className="px-3 py-2 text-[11px] text-muted-foreground truncate max-w-[140px]">{m.originLocation}</TableCell>
                  <TableCell className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap">
                    {formatDate(m.availableFrom)} – {formatDate(m.availableTo)}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-[11px] font-semibold text-foreground tabular-nums">
                    {m.ratePerMile ? `$${m.ratePerMile.toFixed(2)}` : "—"}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-[11px] text-accent font-medium">{m.matchedLoadRef}</TableCell>
                  <TableCell className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleInvite(m)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2.5 py-1 text-[10px] font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      <Send className="h-3 w-3" />
                      Invite
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// --- Helpers ---

function extractState(location: string): string | null {
  if (!location) return null;
  // Try "City, ST" pattern
  const match = location.match(/,\s*([A-Z]{2})\b/);
  if (match) return match[1];
  // Try standalone 2-letter state code
  const stateMatch = location.match(/\b([A-Z]{2})\b/);
  return stateMatch ? stateMatch[1] : null;
}

function formatEquipment(eq: string): string {
  const map: Record<string, string> = {
    van: "Van", reefer: "Reefer", flatbed: "Flatbed",
    step_deck: "Step Deck", power_only: "Power Only",
    box_truck: "Box Truck", hotshot: "Hotshot",
    conestoga: "Conestoga", rgn: "RGN", tanker: "Tanker",
    intermodal: "Intermodal", car_hauler: "Car Hauler",
    dump_trailer: "Dump", lowboy: "Lowboy", other: "Other",
  };
  return map[eq] || eq;
}

function formatDate(d: string): string {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}
